// src/components/KitchenDisplay.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from 'react-router-dom';

const url = process.env.REACT_APP_SERVER_URL || "https://centralsoft.com.my";

// Helper functions
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const safeToFixed = (value, decimals = 2) => {
  const num = safeNumber(value);
  return num.toFixed(decimals);
};

export default function KitchenDisplay() {
  const stallID = localStorage.getItem("stallID");
  const stallName = localStorage.getItem("username");
  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // FIXED: Image URL helper with better error handling
  const getImageUrl = (imageURL) => {
    const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236c757d'%3ENo Image%3C/text%3E%3C/svg%3E";

    // Check if imageURL is invalid
    if (!imageURL ||
        imageURL === "null" ||
        imageURL === "undefined" ||
        imageURL === "false" ||
        imageURL === "" ||
        imageURL === "N/A") {
      return PLACEHOLDER;
    }

    // If it's already a full URL or data URL, return as is
    if (imageURL.startsWith('http') || imageURL.startsWith('data:')) {
      return imageURL;
    }

    // If it's a relative path starting with /, use it as is
    if (imageURL.startsWith('/')) {
      return `${url}${imageURL}`;
    }

    // For other cases, assume it's a filename and construct the URL
    const cleanImageURL = imageURL.replace(/^\//, '').trim();

    // If it already contains a path, use it as is
    if (cleanImageURL.includes('/')) {
      return `${url}/${cleanImageURL}`;
    }

    // Otherwise, assume it's in food-images directory
    return `${url}/food-images/${cleanImageURL}`;
  };

  // Navigation
  const navigateToCustomerMenu = () => {
    navigate('/ordering');
  };

  // Function to generate order summary for header
  const getOrderSummary = (order) => {
    if (!order.items || order.items.length === 0) {
      return "Empty Order";
    }

    const itemSummaries = order.items.map(item => {
      let summary = `${item.quantity}x ${item.foodName}`;

      // Add addons if any
      if (item.selectedAddons && item.selectedAddons.length > 0) {
        const addonText = item.selectedAddons.map(addon =>
          `+ ${addon.addonQuantity}x ${addon.addonName}`
        ).join(', ');
        summary += ` (${addonText})`;
      }

      return summary;
    });

    return itemSummaries.join(' | ');
  };

  // Fetch all data
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch orders
      const ordersResponse = await axios.get(`${url}/api/admin/orders/pending`, {
        params: {
          stallID,
          status: ['pending', 'preparing', 'ready', 'delivered']
        }
      });


      let ordersData = ordersResponse.data.orders || ordersResponse.data || [];
      console.log('Fetched orders:', ordersData);

  // ✅ Filter only allowed statuses
const allowedStatuses = ['pending', 'preparing', 'ready', 'delivered'];
ordersData = ordersData.filter(order =>
  allowedStatuses.includes(order.status?.toLowerCase())
);

if (ordersData.length === 0) {
  console.log('No orders with allowed statuses');
  setOrders([]);
  setLoading(false);
  return;
}




      // Fetch foods data for images
      const foodsResponse = await axios.get(`${url}/api/admin/foods`, {
        params: { stallID }
      });
      const foodsData = foodsResponse.data || [];
      console.log('Fetched foods:', foodsData);

      // Process each order
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          try {
            // Fetch order items
            const itemsResponse = await axios.get(`${url}/api/admin/order_items`, {
              params: {
                stallID: stallID,
                orderID: order.orderID
              }
            });

            console.log('Fetched items response for order', order.orderID, ':', itemsResponse.data);

            // FIXED: Check both data.data and data structure
            const itemsData = itemsResponse.data.data || itemsResponse.data.items || itemsResponse.data || [];
            console.log('Items data for order', order.orderID, ':', itemsData);

            // Process each item
            const itemsWithDetails = await Promise.all(
              itemsData.map(async (item) => {
                try {
                  // Fetch addons for this item
                  const addonsResponse = await axios.get(`${url}/api/admin/order_item_addons`, {
                    params: { orderItemID: item.orderItemID }
                  });

                  console.log('Fetched addons for item', item.orderItemID, ':', addonsResponse.data);

                  const addonsData = addonsResponse.data.addons || addonsResponse.data.data || addonsResponse.data || [];

                  // Find food data for image - FIXED: better matching
                  const foodData = foodsData.find(f => {
                    const foodId = f.id || f.ID || f._id;
                    const itemFoodId = item.foodID || item.foodId;
                    return String(foodId) === String(itemFoodId);
                  });

                  console.log('Found food data for item', item.orderItemID, ':', foodData);

                  const foodImage = foodData ? getImageUrl(foodData.imageURL || foodData.image) : getImageUrl(null);

                  console.log('Generated image URL for item', item.orderItemID, ':', foodImage);

                  // Calculate totals
                  const addonsTotal = addonsData.reduce((total, addon) =>
                    total + (safeNumber(addon.addonPrice) * safeNumber(addon.addonQuantity, 1)), 0
                  );

                  const quantity = safeNumber(item.quantity, 1);
                  const storedTotalPrice = safeNumber(item.totalPrice);
                  const baseTotal = storedTotalPrice - addonsTotal;
                  const unitPrice = quantity > 0 ? baseTotal / quantity : 0;

                  return {
                    orderItemID: item.orderItemID,
                    foodID: item.foodID,
                    foodName: item.foodName || "Unknown Item",
                    foodImage: foodImage,
                    quantity: quantity,
                    unitPrice: unitPrice,
                    totalPrice: baseTotal,
                    notes: item.notes || "",
                    status: item.status || 'pending',
                    selectedAddons: addonsData.map(addon => ({
                      addonID: addon.addonID,
                      addonName: addon.addonName || "Unknown Addon",
                      addonQuantity: safeNumber(addon.addonQuantity, 1),
                      addonPrice: safeNumber(addon.addonPrice),
                      addonTotal: safeNumber(addon.addonPrice) * safeNumber(addon.addonQuantity, 1)
                    })),
                    addonsTotal: addonsTotal,
                    finalPrice: storedTotalPrice
                  };
                } catch (error) {
                  console.error('Error fetching addons for item:', item.orderItemID, error);
                  return {
                    orderItemID: item.orderItemID,
                    foodID: item.foodID,
                    foodName: item.foodName || "Unknown Item",
                    foodImage: getImageUrl(null),
                    quantity: safeNumber(item.quantity, 1),
                    unitPrice: safeNumber(item.unitPrice),
                    totalPrice: safeNumber(item.totalPrice),
                    notes: item.notes || "",
                    status: item.status || 'pending',
                    selectedAddons: [],
                    addonsTotal: 0,
                    finalPrice: safeNumber(item.totalPrice)
                  };
                }
              })
            );

            const orderTotal = itemsWithDetails.reduce((total, item) => total + item.finalPrice, 0);

            return {
              orderID: order.orderID,
              stallID: order.stallID,
              tableNumber: order.tableNumber || 'Unknown',
              status: order.status || 'pending',
              orderDate: order.orderDate,
              time: new Date(order.orderDate).toLocaleTimeString('en-MY', {
                hour: '2-digit',
                minute: '2-digit'
              }),
              date: new Date(order.orderDate).toLocaleDateString('en-MY'),
              items: itemsWithDetails,
              total: `RM ${safeToFixed(orderTotal)}`,
              originalTotal: orderTotal,
              orderSummary: getOrderSummary({ items: itemsWithDetails })
            };
          } catch (error) {
            console.error('Error processing order:', order.orderID, error);
            return {
              orderID: order.orderID,
              stallID: order.stallID,
              tableNumber: order.tableNumber || 'Unknown',
              status: order.status || 'pending',
              orderDate: order.orderDate,
              time: new Date(order.orderDate).toLocaleTimeString('en-MY'),
              date: new Date(order.orderDate).toLocaleDateString('en-MY'),
              items: [],
              total: `RM 0.00`,
              originalTotal: 0,
              orderSummary: "Empty Order"
            };
          }
        })
      );

      setOrders(ordersWithDetails);
      console.log('Final orders with details:', ordersWithDetails);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [stallID]);

  // Auto-refresh
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // FIXED: Update item status function with proper workflow
  const updateItemStatus = async (orderID, orderItemID, newStatus) => {
    try {
      console.log('Updating item status:', { orderID, orderItemID, newStatus });

      // Update item status in backend
      await axios.put(`${url}/api/admin/order_items_status/${orderItemID}`, {
        status: newStatus
      });

      // Update state optimistically
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order => {
          if (order.orderID === orderID) {
            const updatedItems = order.items.map(item =>
              item.orderItemID === orderItemID
                ? { ...item, status: newStatus }
                : item
            );

            return {
              ...order,
              items: updatedItems
            };
          }
          return order;
        });

        return updatedOrders;
      });

    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status: ' + (error.response?.data?.error || error.message));
      // Refresh to get correct state from server
      fetchOrders();
    }
  };

  // FIXED: Action functions with proper status workflow
  const markItemPreparing = (orderID, orderItemID) => {
    updateItemStatus(orderID, orderItemID, 'preparing');
  };

  const markItemReady = (orderID, orderItemID) => {
    updateItemStatus(orderID, orderItemID, 'ready');
  };

  const markItemDelivered = (orderID, orderItemID) => {
    updateItemStatus(orderID, orderItemID, 'delivered');
  };

  const completeOrder = async (orderID) => {
    try {
      await axios.put(`${url}/api/admin/orders/status`, {
        stallID: stallID,
        orderID: orderID,
        status: 'cancelled'
      });

      setOrders(prevOrders => prevOrders.filter(order => order.orderID !== orderID));
    } catch (error) {
      console.error('Error completing order:', error);
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  // Filter orders
  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter(order => order.status === activeFilter);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading kitchen orders...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="bg-dark text-white p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-0">👨‍🍳 Kitchen Display</h1>
            <p className="mb-0">Stall: <strong>{stallName}</strong> | Orders: {orders.length}</p>
          </div>
          <div>
            <button className="btn btn-primary me-2" onClick={fetchOrders}>
              🔄 Refresh
            </button>
            <button className="btn btn-light" onClick={navigateToCustomerMenu}>
              ← Ordering
            </button>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="btn-group" role="group">
            <button className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveFilter('all')}>
              All ({orders.length})
            </button>
            <button className={`btn ${activeFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setActiveFilter('pending')}>
              Pending ({orders.filter(o => o.status === 'pending').length})
            </button>
            <button className={`btn ${activeFilter === 'preparing' ? 'btn-info' : 'btn-outline-info'}`}
              onClick={() => setActiveFilter('preparing')}>
              Preparing ({orders.filter(o => o.status === 'preparing').length})
            </button>
            <button className={`btn ${activeFilter === 'ready' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setActiveFilter('ready')}>
              Ready ({orders.filter(o => o.status === 'ready').length})
            </button>
            <button className={`btn ${activeFilter === 'completed' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setActiveFilter('completed')}>
              Completed ({orders.filter(o => o.status === 'completed').length})
            </button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="row">
        {filteredOrders.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info text-center">
              <h4>No {activeFilter !== 'all' ? activeFilter : ''} orders</h4>
              <p>When new orders come in, they will appear here.</p>
            </div>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.orderID} className="col-lg-6 col-xl-4 mb-4">
              <div className={`card h-100 ${
                order.status === 'pending' ? 'border-warning' :
                order.status === 'preparing' ? 'border-info' :
                order.status === 'ready' ? 'border-success' : 'border-secondary'
              }`}>

                {/* Order Header */}
                <div className={`card-header text-white ${
                  order.status === 'pending' ? 'bg-warning' :
                  order.status === 'preparing' ? 'bg-info' :
                  order.status === 'ready' ? 'bg-success' : 'bg-secondary'
                }`}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h5 className="mb-1">Table {order.tableNumber}</h5>
                      <div className="order-summary">
                        <small className="fw-bold">Order Items:</small>
                        <div className="mt-1">
                          <small>{order.orderSummary}</small>
                        </div>
                      </div>
                      <small className="text-light opacity-75">{order.time}</small>
                    </div>
                    <div className="text-end ms-2">
                      <div className="badge bg-light text-dark mb-2">{order.status.toUpperCase()}</div>
                      <div className="fw-bold">{order.total}</div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="card-body">
                  {order.items && order.items.map(item => (
                    <div key={item.orderItemID} className="mb-3 pb-3 border-bottom">

                      {/* Item Header with Image */}
                      <div className="d-flex align-items-start mb-2">
                        <div className="me-3" style={{ width: '60px', height: '60px', flexShrink: 0 }}>
                          <img
                            src={item.foodImage}
                            alt={item.foodName}
                            className="img-thumbnail"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              console.log('Image failed to load:', item.foodImage);
                              e.target.src = getImageUrl(null);
                            }}
                          />
                        </div>

                        <div className="flex-grow-1">
                          <h6 className="fw-bold text-primary mb-1">
                            {item.quantity}x {item.foodName || 'Unknown Item'}
                          </h6>

                          {/* Addons */}
                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <div className="mb-2">
                              <small className="text-muted fw-bold">Add-ons:</small>
                              {item.selectedAddons.map((addon, idx) => (
                                <div key={idx} className="ms-2">
                                  <small className="fw-semibold">
                                    + {addon.addonQuantity}x {addon.addonName}
                                  </small>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Notes */}
                          {item.notes && (
                            <div className="bg-warning bg-opacity-10 p-2 border-start border-warning border-3">
                              <small className="fw-bold text-dark">
                                📝 Note: {item.notes}
                              </small>
                            </div>
                          )}
                        </div>

                        <span className={`badge ${
                          item.status === 'pending' ? 'bg-warning' :
                          item.status === 'preparing' ? 'bg-info' :
                          item.status === 'ready' ? 'bg-success' :
                          item.status === 'delivered' ? 'bg-primary' : 'bg-secondary'
                        }`}>
                          {item.status}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="btn-group w-100" role="group">
                        {item.status === 'pending' && (
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => markItemPreparing(order.orderID, item.orderItemID)}
                          >
                            🍳 Start Preparing
                          </button>
                        )}

                        {item.status === 'preparing' && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => markItemReady(order.orderID, item.orderItemID)}
                          >
                            ✅ Mark Ready
                          </button>
                        )}

                        {item.status === 'ready' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => markItemDelivered(order.orderID, item.orderItemID)}
                          >
                            🚚 Mark Delivered
                          </button>
                        )}

                        {item.status === 'delivered' && (
                          <button className="btn btn-secondary btn-sm" disabled>
                            ✓ Delivered
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Complete Order Button */}
                {order.items && order.items.every(item => item.status === 'delivered') && (
                  <div className="card-footer">
                    <button
                      className="btn btn-success w-100"
                      onClick={() => completeOrder(order.orderID)}
                    >
                      🎉 Cancel Order
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="text-center mt-4">
        <small className="text-muted">
          🔄 Auto-refreshes every 30 seconds • Last update: {new Date().toLocaleTimeString()}
        </small>
      </div>
    </div>
  );
}
